import { useEffect } from "react";
import store from "../redux";
import { traceDeviceEvent } from "../util/deviceTrace";

/**
 * Captures the assigned-device state at app-lifecycle moments — NOT just when an
 * API call happens. This answers the "I open the app (or it comes back from the
 * background) and it briefly shows no device / something's wrong with the
 * network" reports by recording exactly what the app knew at:
 *
 *   - app_open  : first mount after a (cold) launch
 *   - resume    : returning to the foreground from the background
 *   - online    : connectivity restored
 *   - offline   : connectivity lost
 *
 * Each snapshot records whether a device is known locally, its connection
 * status, the browser online flag, document visibility, and the polling-timeout
 * flag. Reads the live store directly (no stale closures) and is best-effort —
 * it never throws and never affects rendering. Mount once, high in the member
 * layout, alongside the polling hooks.
 */
export function useDeviceStateLifecycleTrace(): void {
  useEffect(() => {
    const snapshot = () => {
      const state = store.getState();
      const device = state?.clinic?.assignedDevice;
      return {
        hasDevice: !!device,
        deviceId: device?.device_id ?? null,
        deviceConnStatus: device?.device_conn_status ?? null,
        online: typeof navigator !== "undefined" ? navigator.onLine : null,
        visibility:
          typeof document !== "undefined" ? document.visibilityState : null,
        devicePollingTimedOut: state?.clinic?.devicePollingTimedOut ?? null,
        clinicId: state?.clinic?.userClinic?.id ?? null,
        patientId: state?.auth?.user?.patient_id ?? null,
        email: state?.auth?.user?.email ?? null,
      };
    };

    const trace = (phase: "app_open" | "resume" | "online" | "offline") =>
      traceDeviceEvent("device_lifecycle", { phase, ...snapshot() });

    // Cold-start / first-mount snapshot: what did the app know the instant it
    // became interactive (after redux-persist rehydration, before/around the
    // bootstrap fetch)?
    trace("app_open");

    const onVisibility = () => {
      if (document.visibilityState === "visible") trace("resume");
    };
    const onOnline = () => trace("online");
    const onOffline = () => trace("offline");

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);
}
